import { useState, useId } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { object, string, type InferType } from "yup";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertCircle, CheckCircle2, X } from "lucide-react";
import { Alert, AlertDescription, Button, Input, PasswordInput } from "@envirohub/ui";
import { auth, clearPasswordInputs, establishSessionAfterLogin } from "@envirohub/utils";
import { useLogin, useSwitchContext } from "@/api";
import type { LoginResponse, SwitchContextResponse } from "@/api";
import { useAuthError } from "@/hooks/useAuthError";
import { env } from "@/config/env";
import {
  customerContexts as filterCustomerContexts,
  pickContext,
  readLastContextKey,
} from "@/lib/contexts";
import type { InvitationLoginState } from "@/lib/invitations";

const schema = object({
  email: string().required("validation.emailRequired").email("validation.invalidEmail"),
  password: string().required("validation.passwordRequired").min(8, "validation.passwordMin"),
});

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const invitationState = (location.state ?? null) as Partial<InvitationLoginState> | null;
  const resolveAuthError = useAuthError();
  const [serverError, setServerError] = useState<string | null>(null);
  const [isRouting, setIsRouting] = useState(false);
  const formId = useId();

  type FormData = InferType<typeof schema>;

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: { email: invitationState?.email ?? "" },
  });

  const login = useLogin();
  const switchContext = useSwitchContext();

  const redirectTarget = () => {
    const searchParams = new URLSearchParams(location.search);
    const redirectParam = searchParams.get("redirect");
    return redirectParam || location.state?.from?.pathname || "/";
  };

  const applyAuth = (
    authResult: LoginResponse | SwitchContextResponse,
    mustResetPassword: boolean,
  ) => {
    auth.setAuth({
      accessToken: authResult.accessToken,
      refreshToken: authResult.refreshToken,
      expiresIn: authResult.expiresIn,
      activeContext: authResult.activeContext,
      availableContexts: authResult.availableContexts,
      mustResetPassword,
    });
  };

  const onSubmit = (data: FormData) => {
    setServerError(null);
    login.mutate(data, {
      onSuccess: (result) => {
        const customerContexts = filterCustomerContexts(result.availableContexts);
        if (customerContexts.length === 0) {
          setServerError(t("errors.codes.NO_PORTAL_ACCESS"));
          return;
        }

        const completeLogin = async () => {
          setIsRouting(true);
          applyAuth(result, result.mustResetPassword);
          try {
            await establishSessionAfterLogin(env.VITE_API_BASE_URL);
          } catch {
            // Proceed with login tokens if refresh fails.
          }

          if (result.mustResetPassword) {
            clearPasswordInputs();
            navigate("/change-password", { replace: true });
            return;
          }

          // Refresh resets JWT activeContext — re-bind a customer context,
          // preferring the one last chosen in the header switcher.
          const customerContext =
            pickContext(customerContexts, readLastContextKey()) ?? customerContexts[0];
          switchContext.mutate(
            { scope: customerContext.scope, membershipId: customerContext.membershipId },
            {
              onSuccess: (switched) => {
                applyAuth(switched, false);
                navigate(redirectTarget(), { replace: true });
              },
              onError: (error) => {
                setIsRouting(false);
                setServerError(resolveAuthError(error).message);
              },
            },
          );
        };

        completeLogin().catch(() => {
          setIsRouting(false);
          setServerError(t("errors.unknown"));
        });
      },
      onError: (error) => {
        const action = resolveAuthError(error);
        if (action.redirectTo) {
          clearPasswordInputs();
          navigate(action.redirectTo, {
            state: { contact: data.email, redirectTo: "/login" },
          });
          return;
        }

        const fieldKeys = Object.keys(action.fieldErrors);
        if (fieldKeys.length > 0) {
          fieldKeys.forEach((key) => {
            if (key === "email" || key === "password") {
              setError(key, { message: action.fieldErrors[key] });
            }
          });
          return;
        }

        setServerError(action.message);
      },
    });
  };

  const showError = Boolean(serverError);
  const isSubmitting = login.isPending || switchContext.isPending || isRouting;

  return (
    <div className="flex flex-col gap-8">
      <div className="text-center">
        <h1 className="text-h3 font-bold text-surface-on dark:text-white">{t("auth.welcome")}</h1>
        <p className="text-body text-surface-on-variant dark:text-[#C3C9B4] mt-2">
          {t("auth.signInToContinue", { defaultValue: "Sign in to continue" })}
        </p>
      </div>

      {invitationState?.invitationAccepted && !showError && (
        <Alert variant="success">
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>{t("auth.inviteAccept.acceptedLoginHint")}</AlertDescription>
        </Alert>
      )}

      {showError && (
        <Alert variant="destructive" className="ps-10">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{serverError}</AlertDescription>
          <button
            type="button"
            onClick={() => setServerError(null)}
            className="absolute end-3 top-3 rounded-sm opacity-70 transition-opacity hover:opacity-100"
            aria-label={t("common.close")}
          >
            <X className="h-4 w-4" />
          </button>
        </Alert>
      )}

      <div className="flex flex-col gap-5">
        <form
          id={formId}
          noValidate
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-5"
        >
          <Input
            id="email"
            type="email"
            autoComplete="username"
            label={t("auth.email")}
            placeholder={t("auth.emailPlaceholder")}
            error={errors.email?.message ? t(errors.email.message) : undefined}
            className="h-12"
            required
            {...register("email")}
          />

          <PasswordInput
            id="password"
            autoComplete="current-password"
            label={t("auth.password")}
            placeholder={t("auth.passwordPlaceholder")}
            error={errors.password?.message ? t(errors.password.message) : undefined}
            className="h-12"
            required
            {...register("password")}
          />
        </form>

        <div className="flex justify-end -mt-3">
          <Link
            to="/forgot-password"
            onClick={() => clearPasswordInputs()}
            className="text-sm font-medium text-primary no-underline hover:underline transition-all"
          >
            {t("auth.forgotPassword")}
          </Link>
        </div>

        <Button
          type="submit"
          form={formId}
          variant="default"
          size="lg"
          loading={isSubmitting}
          className="w-full mt-4 shadow-lg shadow-primary/25 hover:shadow-primary/40 transition-shadow"
        >
          {t("auth.login")}
        </Button>
      </div>

      <p className="text-body text-surface-on-variant dark:text-[#C3C9B4] text-center mt-2">
        {t("auth.newHere")}{" "}
        <Link
          to="/register"
          onClick={() => clearPasswordInputs()}
          className="text-primary font-bold no-underline hover:underline transition-all"
        >
          {t("auth.register")}
        </Link>
      </p>
    </div>
  );
}
